import React from 'react';
import { Box, Container, Typography, Divider, Link } from '@mui/material';

const CONTACT_EMAIL = 'support@amit-klein.com';
const APP_URL = 'https://media.amit-klein.com';
const LAST_UPDATED = 'March 22, 2026';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" gutterBottom fontWeight={600}>
      {title}
    </Typography>
    {children}
  </Box>
);

const PrivacyPolicyPage = () => (
  <Container maxWidth="md" sx={{ py: 6 }}>
    <Typography variant="h4" fontWeight={700} gutterBottom>
      Privacy Policy
    </Typography>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      Last updated: {LAST_UPDATED}
    </Typography>
    <Divider sx={{ my: 3 }} />

    <Section title="1. Introduction">
      <Typography variant="body1" paragraph>
        Welcome to Amit Klein Media ({APP_URL}). This Privacy Policy explains how we collect,
        use, and protect information when you use our media album management application
        ("the App"). By using the App, you agree to the practices described in this policy.
      </Typography>
    </Section>

    <Section title="2. Information We Collect">
      <Typography variant="body1" paragraph>
        <strong>Account information:</strong> When you sign in with Google we receive your name,
        email address, and profile picture provided by Google OAuth 2.0.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Media files:</strong> Photos and videos you upload are stored securely on Azure
        Blob Storage and are associated with your account.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Session data:</strong> We store album session metadata (event name, dates,
        settings) that you provide while using the App.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Usage data:</strong> We collect anonymised telemetry (page views, feature usage)
        via Azure Application Insights to improve the service. No personally identifiable
        information is included in telemetry events.
      </Typography>
    </Section>

    <Section title="3. How We Use Your Information">
      <Typography component="ul" sx={{ pl: 3 }}>
        {[
          'To authenticate you and provide access to the App.',
          'To store and display your uploaded media within your album sessions.',
          'To run AI-powered features (face recognition, image analysis) on your uploaded photos.',
          'To send transactional notifications related to your account or session activity.',
          'To monitor and improve the reliability and performance of the service.',
        ].map((item) => (
          <Typography key={item} component="li" variant="body1" sx={{ mb: 1 }}>
            {item}
          </Typography>
        ))}
      </Typography>
    </Section>

    <Section title="4. Google OAuth & Third-Party Services">
      <Typography variant="body1" paragraph>
        We use Google OAuth 2.0 for authentication. When you sign in we request only the
        minimum scopes needed: your basic profile and email. We do not access your Google Drive,
        Gmail, or any other Google services beyond authentication.
      </Typography>
      <Typography variant="body1" paragraph>
        Your use of Google sign-in is also governed by{' '}
        <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
          Google's Privacy Policy
        </Link>.
      </Typography>
      <Typography variant="body1" paragraph>
        We use the following third-party services to operate the App:
      </Typography>
      <Typography component="ul" sx={{ pl: 3 }}>
        {[
          'Microsoft Azure (cloud infrastructure, blob storage, container hosting)',
          'AWS Rekognition (face detection — processes photos you upload)',
          'OpenAI / Azure OpenAI (image analysis and embedding generation)',
          'MongoDB Atlas (database)',
          'Azure Application Insights (anonymised telemetry)',
        ].map((item) => (
          <Typography key={item} component="li" variant="body1" sx={{ mb: 1 }}>
            {item}
          </Typography>
        ))}
      </Typography>
    </Section>

    <Section title="5. Data Sharing">
      <Typography variant="body1" paragraph>
        We do not sell, rent, or share your personal information with third parties for
        marketing purposes. Your data is only shared with the service providers listed in
        Section 4 as necessary to operate the App, and only under appropriate data processing
        agreements.
      </Typography>
    </Section>

    <Section title="6. Data Retention">
      <Typography variant="body1" paragraph>
        Your account data and uploaded media are retained for as long as your account is active.
        You may delete your data at any time by contacting us at{' '}
        <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>. We will fulfill deletion
        requests within 30 days.
      </Typography>
    </Section>

    <Section title="7. Security">
      <Typography variant="body1" paragraph>
        We use industry-standard security measures including HTTPS/TLS encryption in transit,
        Azure Active Directory authentication, and role-based access control. No method of
        transmission over the internet is 100% secure; while we strive to protect your data
        we cannot guarantee absolute security.
      </Typography>
    </Section>

    <Section title="8. Children's Privacy">
      <Typography variant="body1" paragraph>
        The App is not directed to children under the age of 13. We do not knowingly collect
        personal information from children. If you believe a child has provided us with their
        information, please contact us and we will delete it promptly.
      </Typography>
    </Section>

    <Section title="9. Your Rights">
      <Typography variant="body1" paragraph>
        Depending on your location you may have the right to access, correct, or delete the
        personal information we hold about you, or to restrict or object to its processing.
        To exercise these rights please contact us at{' '}
        <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
      </Typography>
    </Section>

    <Section title="10. Changes to This Policy">
      <Typography variant="body1" paragraph>
        We may update this Privacy Policy from time to time. The "Last updated" date at the top
        of this page will reflect any changes. Continued use of the App after changes are posted
        constitutes your acceptance of the revised policy.
      </Typography>
    </Section>

    <Section title="11. Contact Us">
      <Typography variant="body1">
        If you have questions or concerns about this Privacy Policy, please contact us:
      </Typography>
      <Typography variant="body1" sx={{ mt: 1 }}>
        <strong>Email:</strong>{' '}
        <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>
      </Typography>
      <Typography variant="body1">
        <strong>Website:</strong>{' '}
        <Link href={APP_URL} target="_blank" rel="noopener noreferrer">
          {APP_URL}
        </Link>
      </Typography>
    </Section>

    <Divider sx={{ my: 3 }} />
    <Typography variant="caption" color="text.secondary">
      © {new Date().getFullYear()} Amit Klein Media. All rights reserved.
    </Typography>
  </Container>
);

export default PrivacyPolicyPage;
